import { GOOD_MATCH_THRESHOLD, STRONG_MATCH_THRESHOLD } from "./coach-live-score";

/**
 * Geometry and colour for the single-donut coach score gauge.
 *
 * Pure arithmetic, deliberately outside the component: the band layout is the part with real rules
 * in it (which colour belongs to which arc, what happens when the projection is below the current
 * score, how a 0-length arc is handled) and those rules are worth pinning with tests rather than
 * re-deriving them inside JSX.
 *
 * WHAT REPLACED WHAT: the panel used to show TWO rings side by side — baseline on the left,
 * projected on the right, an arrow between them. Two rings meant the student read two numbers and
 * worked out the relationship themselves, and neither ring showed how much of the total was already
 * earned versus still on offer. One donut with two bands says it in a single shape: how far you are,
 * and how much further the suggestions can take you.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/**
 * Tier palette, shared with the student app's MatchScoreBadge / ScoreBreakdownDrawer.
 *
 * Hex rather than the semantic Tailwind classes because these values are also fed to SVG `stroke`,
 * and because the student's job card uses these exact three — a résumé that reads "Good Match"
 * yellow in the builder must not read as something else on the card it came from.
 */
export const TIER_COLORS = {
  strong: "#5AF28E",
  good: "#FFE836",
  stretch: "#DA6C5D",
} as const;

/** Colour for a score, by the same two cutoffs that produce its label. */
export const tierColorOf = (score: number) => {
  if (score >= STRONG_MATCH_THRESHOLD) return TIER_COLORS.strong;
  if (score >= GOOD_MATCH_THRESHOLD) return TIER_COLORS.good;
  return TIER_COLORS.stretch;
};

export const clampPercent = (value: number) => Math.min(100, Math.max(0, Number(value) || 0));

export type ScoreBandKey = "current" | "projected";

export type ScoreBand = {
  key: ScoreBandKey;
  /**
   * Percentage the arc starts at, measured clockwise from twelve o'clock.
   *
   * Both bands start at 0 — they are stacked, not adjacent. See buildScoreBands.
   */
  from: number;
  /** Percentage the arc ends at. */
  to: number;
  /** Exact student-side tier colour. Never tinted or faded — see buildScoreBands. */
  color: string;
};

/**
 * Unfilled remainder of the ring. The student badge's own track colour (`#353535`), not a theme
 * token — `foreground/8%` was close but not the same, and the builder's donut IS that badge,
 * mid-edit.
 */
export const TRACK_COLOR = "#353535";

/**
 * Stroke opacity of the reachable arc, so it reads as an offer rather than a reading.
 *
 * Applied by the donut, NOT stored on the band: `ScoreBand.color` stays the exact student-side hex,
 * which is what `coach-score-visuals.spec.ts` pins. Dulling is a rendering decision, not part of the
 * colour rule.
 *
 * ⚠️ DULLING A HUE OVER A NEAR-BLACK RAIL DARKENS IT — that is unavoidable, not a bug: `#FFE836` at
 * reduced opacity reads olive, `#5AF28E` reads moss, `#DA6C5D` reads brick. 0.55 is the highest value
 * that still reads as inactive next to the full-strength earned arc; earlier passes at 0.32 and 0.45
 * pushed yellow and red close enough to each other to be confusable, which is the failure worth
 * avoiding.
 */
export const PROJECTED_BAND_OPACITY = 0.55;

/**
 * The one or two arcs that make up the gauge.
 *
 * The colour rule is the point of this function: the earned arc is coloured by the CURRENT score's
 * tier, and the reachable arc by the PROJECTED total's tier. So a student sitting at 10 with a
 * reachable 52 sees red up to 10 and yellow from 10 to 52; if 80 were reachable, that second arc
 * would be green. It answers "what would this résumé become", which is the question the suggestions
 * are there to answer — colouring it by the current tier instead would show the student the colour
 * they are trying to escape.
 *
 * ⚠️ THE ARCS ARE SEPARATED BY HUE AND OPACITY ONLY — nothing structural. Two other attempts are
 * worth not repeating:
 *
 *   1. **A blank gap between the arcs.** Made the ring look broken rather than segmented.
 *   2. **A thinner stroke on the reachable arc.** Notched the ring's outline; it read as a defect in
 *      the component, not as a second state.
 *
 * `color` here is always the exact student-side hex. The dulling of the reachable arc is applied by
 * the donut via PROJECTED_BAND_OPACITY, so the colour rule stays testable independently of how it is
 * rendered.
 *
 * ⚠️ BOTH BANDS ALWAYS EXIST, AND THEY OVERLAP — THE EARNED ARC COVERS THE REACHABLE ONE.
 *
 * This is the fix for a visibly broken animation on the accept that finishes the plan. The bands used
 * to sit END TO END (`0 → earned`, then `earned → reachable`) and the reachable one was omitted once
 * `earned` caught up. Two things went wrong at that moment, both because the arc was a React element
 * keyed by band:
 *
 *   - omitting it UNMOUNTED the `<circle>`, so the reachable arc did not shrink to nothing — it
 *     vanished in one frame while the earned arc was still animating into the space it had left. The
 *     ring flashed short and then grew, instead of one arc filling up.
 *   - the reverse, on Reject, MOUNTED a new circle already at its final geometry. CSS has no start
 *     value to transition from on mount, so the reachable arc snapped in at full length.
 *
 * Now the reachable arc spans `0 → reachable` and the earned arc spans `0 → earned` on top of it.
 * Neither ever unmounts, both ends of every transition are real geometry, and "accepting fills the
 * ring" becomes literally what the markup does: the opaque arc grows over the dulled one until it
 * hides it completely. At full acceptance the two are the same length, so the reachable arc is still
 * there and still animating — just entirely covered.
 *
 * ⚠️ ORDER IS PAINT ORDER — reachable first, earned second. Reversing it would draw the offer over the
 * achievement. The donut must not sort this array; it looks bands up by `key`.
 *
 * `projected` below `current` clamps the reachable arc UP to `earned` rather than drawing backwards.
 * That happens when accepted ids outlive the plan they came from (a stale sessionStorage run), and a
 * reversed arc would render as a full circle.
 */
export const buildScoreBands = ({
  current,
  projected,
}: {
  current: number;
  projected: number;
}): ScoreBand[] => {
  const earned = clampPercent(current);
  const reachable = Math.max(earned, clampPercent(projected));

  return [
    { key: "projected", from: 0, to: reachable, color: tierColorOf(reachable) },
    { key: "current", from: 0, to: earned, color: tierColorOf(earned) },
  ];
};

export type ArcDash = { strokeDasharray: string; strokeDashoffset: number };

/**
 * `stroke-dasharray` / `stroke-dashoffset` for one arc of a circle.
 *
 * One visible dash of the arc's own length followed by a gap of a full circumference, shifted back
 * by the start offset. This is what lets several arcs share one `<circle>` radius and sit end to end
 * without any of them being a separate path — and, unlike stacking full-length arcs on top of each
 * other, each band keeps its own opacity instead of compositing with the one beneath it.
 *
 * A zero-length arc returns an empty dash rather than `0 C`, because a round line cap on a
 * zero-length dash still paints a dot — a student at 0% would see a stray pip at twelve o'clock.
 */
export const arcDash = ({
  from,
  to,
  circumference,
}: {
  from: number;
  to: number;
  circumference: number;
}): ArcDash => {
  const length = (Math.max(0, to - from) / 100) * circumference;

  if (length <= 0) return { strokeDasharray: `0 ${circumference * 2}`, strokeDashoffset: 0 };

  const start = (Math.max(0, from) / 100) * circumference;

  return {
    strokeDasharray: `${length} ${circumference}`,
    // Negative: dashoffset shifts the pattern backwards along the path, so a positive start
    // percentage moves the dash forwards. `|| 0` normalises the -0 that `-(0)` produces — harmless
    // in SVG, but it makes the value awkward to assert on and to compare between renders.
    strokeDashoffset: -start || 0,
  };
};
